import { useState } from 'react';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { KEY_CODES } from '@/main/app.constants';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { IconDefinition } from '@seeqdev/icons';

export interface IconWithManagedSpinnerProps {
  icon?: IconDefinition;
  action: (e: React.MouseEvent | React.KeyboardEvent) => Promise<any>;
  tooltip: string;
  tooltipDelay?: number;
  actionOnEnter?: boolean;
  large?: boolean;
  white?: boolean;
  extraClassNames?: string;
  xstyle?: StyleXStyles;
  /** Icon styles applied only while idle, such as hover/focus feedback excluded during loading. */
  activeXstyle?: StyleXStyles;
  disabled?: boolean;
  testId?: string;
}

/** Icon that shows spinner when click action is in progress */
export const IconWithManagedSpinner = (props: IconWithManagedSpinnerProps) => {
  const { action, tooltip, tooltipDelay = 0, disabled = false } = props;
  const [spinning, setSpinning] = useState(false);

  const clickFunction = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    setSpinning(true);
    return action(e).finally(() => setSpinning(false));
  };

  return (
    <HoverTooltip text={tooltip} delay={tooltipDelay}>
      <div
        data-testid={props.testId ? `iconControl-${props.testId}` : undefined}
        aria-disabled={disabled}
        onClick={clickFunction}
        onKeyUp={(e) => e.keyCode === KEY_CODES.ENTER && clickFunction(e)}>
        <IconWithSpinner {...props} xstyle={[props.xstyle, !spinning && props.activeXstyle]} spinning={spinning} />
      </div>
    </HoverTooltip>
  );
};
